import * as React from 'react';
import { connect } from 'react-redux';
import flattenDeep from 'lodash/flattenDeep';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import DeliverableMediaAttachment from 'modules/deliverables/components/media/parts/DeliverableMediaAttachment';
import DeliverableMediaAttachments from 'modules/deliverables/components/media/parts/DeliverableMediaAttachments';
import DeliverableMediaPostForm from 'modules/deliverables/components/media/parts/DeliverableMediaPostForm';
import DeliverableMediaUploader from 'modules/deliverables/components/media/parts/DeliverableMediaUploader';

import { removeDeliverableAttachment } from 'modules/deliverables/reducers/deliverableMediaAttachments';
import { removeDeliverablePost } from 'modules/subcampaigns/reducers/offerDeliverables';
import { trackEventDeliverableUploaded } from 'modules/tracking/utils/tracking';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';

import {
  DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS,
  DELIVERABLE_ATTACHMENT_TYPE_HIGHRES,
  DELIVERABLE_TYPE_INSTAGRAM_REEL,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_TIKTOK_VIDEO,
  filterAttachmentsByType,
  isInstagramPost,
  isInstagramStory
} from 'utils/deliverables';

import { isInstagramPostUrl, isInstagramReelUrl, isValidTikTokVideoUrl } from 'utils/validators';
import { normalizeInstagramUrl, normalizeTikTokUrl } from 'utils/url';
import { __t, applyTranslations } from 'utils/language';

import type {
  Deliverable,
  DeliverableAttachment,
  DeliverableUploadedEventLabel
} from 'types';

import './deliverables-metrics-attachment-form.scss';

applyTranslations(require('./deliverables-metrics-attachment-form.i18n.json'));

type IdOrTmpId = number | string;

type OwnProps = {
  deliverable: Deliverable,
  deliverableUploadedEventLabel: DeliverableUploadedEventLabel,
  header?: string,
  offerId: number,
  hideAnalyticsColumn?: boolean,
  onUpdate?: () => void
};

type DispatchProps = {
  dispatchShowConfirmationModal: (
    params: {
      message: string,
      confirmButtonLabel: string,
      onConfirm: () => void,
      onCancel: () => void
    }
  ) => void,
  dispatchHideConfirmationModal: () => void,
  dispatchRemoveDeliverablePost: (offerId: number, deliverableId: number) => Promise<void>,
  dispatchRemoveDeliverableAttachment: (offerId: number, deliverableId: number, attachmentId: IdOrTmpId) => Promise<void>
};

type Props = OwnProps & DispatchProps;

class DeliverablesMetricsAttachmentForm extends React.Component<Props> {
  private handleDeletePost = () => {
    const {
      dispatchShowConfirmationModal,
      dispatchHideConfirmationModal,
      dispatchRemoveDeliverablePost,
      offerId,
      deliverable
    } = this.props;

    dispatchShowConfirmationModal({
      message: __t('deliverables_metrics_attachment_form.post_delete_confirmation.message'),
      confirmButtonLabel: __t('deliverables_metrics_attachment_form.post_delete_confirmation.button_confirm'),
      onConfirm: () =>
        dispatchRemoveDeliverablePost(offerId, deliverable.id)
          .then(() => dispatchHideConfirmationModal()),
      onCancel: () => dispatchHideConfirmationModal()
    });
  };

  private handleDeleteAttachment = (attachment: DeliverableAttachment): void => {
    const {
      dispatchShowConfirmationModal,
      dispatchHideConfirmationModal,
      dispatchRemoveDeliverableAttachment,
      onUpdate,
      offerId,
      deliverable
    } = this.props;

    dispatchShowConfirmationModal({
      message: __t('deliverables_metrics_attachment_form.attachment_delete_confirmation.message'),
      confirmButtonLabel: __t('deliverables_metrics_attachment_form.attachment_delete_confirmation.button_confirm'),
      onConfirm: () =>
        dispatchRemoveDeliverableAttachment(offerId, deliverable.id, attachment.id)
          .then(() => onUpdate && onUpdate())
          .then(() => dispatchHideConfirmationModal()),
      onCancel: () => dispatchHideConfirmationModal()
    });
  };

  private getMatchedAttachmentIds(): Array<number> {
    const analytics = this.props.deliverable.analytics || [];
    const matchedAttachments = analytics
      .map((entry) => (('matchedAttachments' in entry)
        ? entry.matchedAttachments || []
        : [])
      );
    return flattenDeep(matchedAttachments);
  }

  private renderPost(): React.ReactNode {
    const { offerId, deliverable, onUpdate, deliverableUploadedEventLabel } = this.props;
    const post = deliverable.post;

    return (
      <div className="deliverables-metrics-attachment-form__post">
        <div className="deliverables-metrics-attachment-form__title">
          {__t('deliverables_metrics_attachment_form.post')}
        </div>
        {
          !isEmpty(post) && !isNil(post) ?
            <DeliverableMediaAttachment
              attachment={{
                id: post.id,
                externalUrl: post.url,
                url: post.thumbnailUrl,
                assetType: 'image',
                meta: {
                  type: DELIVERABLE_ATTACHMENT_TYPE_HIGHRES
                }
              }}
              isImagePreviewEnabled
              onDeleteAttachment={this.handleDeletePost}
            /> :
            <DeliverableMediaPostForm
              canUpload
              offerId={offerId}
              deliverableId={deliverable.id}
              placeholder="https://www.instagram.com/p/yourpost/"
              postUrlValidator={isInstagramPostUrl}
              normalizePostUrl={normalizeInstagramUrl}
              onUpdate={onUpdate}
              onUploadModeTracking={
                () => trackEventDeliverableUploaded('post url', deliverableUploadedEventLabel)
              }
            />
        }
      </div>
    );
  }

  private renderReel(): React.ReactNode {
    const { offerId, deliverable, onUpdate, deliverableUploadedEventLabel } = this.props;
    const post = deliverable.post;

    return (
      <div className="deliverables-metrics-attachment-form__post">
        <div className="deliverables-metrics-attachment-form__title">
          {__t('deliverables_metrics_attachment_form.reel')}
        </div>
        {
          !isEmpty(post) && !isNil(post) ?
            <DeliverableMediaAttachment
              attachment={{
                id: post.id,
                externalUrl: post.url,
                url: post.thumbnailUrl,
                assetType: 'image',
                meta: {
                  type: DELIVERABLE_ATTACHMENT_TYPE_HIGHRES
                }
              }}
              width={87}
              height={154}
              onDeleteAttachment={this.handleDeletePost}
            /> :
            <DeliverableMediaPostForm
              canUpload
              offerId={offerId}
              deliverableId={deliverable.id}
              placeholder="https://www.instagram.com/reel/yourpost/"
              postUrlValidator={isInstagramReelUrl}
              normalizePostUrl={normalizeInstagramUrl}
              onUpdate={onUpdate}
              onUploadModeTracking={
                () => trackEventDeliverableUploaded('reel url', deliverableUploadedEventLabel)
              }
            />
        }
      </div>
    );
  }

  private renderStory(): React.ReactNode {
    const { offerId, deliverable, onUpdate, deliverableUploadedEventLabel } = this.props;
    const attachments = filterAttachmentsByType(deliverable.postAttachments, DELIVERABLE_ATTACHMENT_TYPE_HIGHRES);
    // all ids are converted to string to match also with numbers
    const matchedAttachments = this.getMatchedAttachmentIds().map(id => String(id));
    const attachmentsToRender = attachments.filter(entry => !matchedAttachments.includes(String(entry.id)));

    return (
      <div className="deliverables-metrics-attachment-form__post">
        <div className="deliverables-metrics-attachment-form__title">
          {__t('deliverables_metrics_attachment_form.story')}
        </div>
        <DeliverableMediaAttachments
          canDragAndDrop
          canUpload
          placeholderForUpload={(
            <DeliverableMediaUploader
              offerId={offerId}
              deliverable={deliverable}
              attachmentType={DELIVERABLE_ATTACHMENT_TYPE_HIGHRES}
              onUpdate={onUpdate}
              onUploadModeTracking={
                () => trackEventDeliverableUploaded('story snippet', deliverableUploadedEventLabel)
              }
            />
          )}
          attachments={attachmentsToRender}
          isImagePreviewEnabled
          onDeleteAttachment={this.handleDeleteAttachment}
        />
      </div>
    );
  }

  private renderTikTokVideo() {
    const { offerId, deliverable, onUpdate, deliverableUploadedEventLabel } = this.props;
    const post = deliverable.post;
    const height = (deliverable.deliverableType === DELIVERABLE_TYPE_TIKTOK_VIDEO) ? 170 : 85;

    return (
      <div className="deliverables-metrics-attachment-form__post">
        <div className="deliverables-metrics-attachment-form__title">
          {__t('deliverables_metrics_attachment_form.tiktok')}
        </div>
        {
          post ?
            <DeliverableMediaAttachment
              attachment={{
                id: post.id,
                externalUrl: post.url,
                url: post.thumbnailUrl,
                assetType: 'image',
                meta: {
                  type: DELIVERABLE_ATTACHMENT_TYPE_HIGHRES
                }
              }}
              width={85}
              height={height}
              isImagePreviewEnabled
              onDeleteAttachment={this.handleDeletePost}
            /> :
            <DeliverableMediaPostForm
              canUpload
              offerId={offerId}
              deliverableId={deliverable.id}
              placeholder="https://www.tiktok.com/@user/video/1234567890"
              postUrlValidator={isValidTikTokVideoUrl}
              normalizePostUrl={normalizeTikTokUrl}
              onUpdate={onUpdate}
              onUploadModeTracking={
                () => trackEventDeliverableUploaded('tiktok video url', deliverableUploadedEventLabel)
              }
            />
        }
      </div>
    );
  }

  private renderAttachments(): React.ReactNode {
    const { offerId, deliverable, onUpdate, deliverableUploadedEventLabel } = this.props;
    const attachments = filterAttachmentsByType(deliverable.postAttachments, DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS);
    // all ids are converted to string to match also with numbers
    const matchedAttachments = this.getMatchedAttachmentIds().map(id => String(id));
    const attachmentsToRender = attachments.filter(entry => !matchedAttachments.includes(String(entry.id)));

    return (
      <div className="deliverables-metrics-attachment-form__attachments">
        <div className="deliverables-metrics-attachment-form__title">
          {__t('deliverables_metrics_attachment_form.attachment')}
        </div>
        <DeliverableMediaAttachments
          canDragAndDrop={isInstagramStory(deliverable.deliverableType)}
          canUpload
          placeholderForUpload={(
            <DeliverableMediaUploader
              ignoreMetricsCreation={deliverable.deliverableType === DELIVERABLE_TYPE_INSTAGRAM_STORY}
              offerId={offerId}
              deliverable={deliverable}
              attachmentType={DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS}
              onUpdate={onUpdate}
              onUploadModeTracking={() => {
                if (deliverable.deliverableType.indexOf('story') > -1) {
                  trackEventDeliverableUploaded(
                    'story analytics screenshot',
                    deliverableUploadedEventLabel
                  );
                }

                if (deliverable.deliverableType.indexOf('post') > -1) {
                  trackEventDeliverableUploaded(
                    'post analytics screenshot',
                    deliverableUploadedEventLabel
                  );
                }

                if (deliverable.deliverableType.indexOf('tiktok') > -1) {
                  trackEventDeliverableUploaded(
                    'tiktok analytics screenshot',
                    deliverableUploadedEventLabel
                  );
                }
              }}
            />
          )}
          attachments={attachmentsToRender}
          isImagePreviewEnabled
          onDeleteAttachment={this.handleDeleteAttachment}
        />
      </div>
    );
  }

  private renderHeader() {
    const { header } = this.props;

    if (!header) {
      return null;
    }

    return (
      <div className="deliverables-metrics-attachment-form__header">
        {header}
      </div>
    );
  }

  render(): React.ReactNode {
    const { deliverable, hideAnalyticsColumn } = this.props;

    return (
      <div className="deliverables-metrics-attachment-form">
        {this.renderHeader()}
        <div className="deliverable-media-post-form__content">
          { isInstagramPost(deliverable.deliverableType) && this.renderPost() }
          { isInstagramStory(deliverable.deliverableType) && this.renderStory() }
          { deliverable.deliverableType === DELIVERABLE_TYPE_INSTAGRAM_REEL && this.renderReel() }
          { deliverable.deliverableType === DELIVERABLE_TYPE_TIKTOK_VIDEO && this.renderTikTokVideo() }
          { !hideAnalyticsColumn && this.renderAttachments()}
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchShowConfirmationModal: (params: {
    message: string
  }) => dispatch(showConfirmModal(params)),

  dispatchHideConfirmationModal: () => dispatch(hideConfirmModal()),

  dispatchRemoveDeliverablePost: (offerId: number, deliverableId: number) => (
    dispatch(removeDeliverablePost(offerId, deliverableId))
  ),

  dispatchRemoveDeliverableAttachment: (offerId, deliverableId, attachmentId) => (
    dispatch(removeDeliverableAttachment(offerId, deliverableId, attachmentId))
  )
});

export default connect(null, mapDispatchToProps)(DeliverablesMetricsAttachmentForm);
